<script lang="ts">
  import IconGen from "./icon-gen.svelte";
  import { utils } from "$lib/utils";
  import { Spinner } from "@thehood/svlib";
  import Suggestion from "$components/promo/suggestion.svelte";
  import type { INewProductUI } from "../+page.server";
  import { onDestroy, onMount } from "svelte";

  export let item: INewProductUI;
  export let locale: string;
  export let showTicketBuyFn: (item: INewProductUI) => void;
  export let loading: boolean;

  const {
    qty,
    block,
    currency,
    productId,
    offerPrice,
    proceedPrice,
    ticketCategory,
    ticketFormat,
    limitedAvailability,
  } = item;
  const { value, colour, title } = ticketCategory;

  function addTicket(ev: Event) {
    ev.stopImmediatePropagation();
    showTicketBuyFn(item);
  }

  let liEl: HTMLElement;
  onMount(() => liEl && liEl.addEventListener("click", addTicket));
  onDestroy(() => liEl && liEl.removeEventListener("click", addTicket));
</script>

<li
  class="single-ticket grid"
  data-productid={productId}
  data-availability={qty}
  data-section={value}
  data-block={block.map(b => b.toLowerCase()).join(", ")}
  data-proceedprice={proceedPrice}
  role="menuitem"
  class:loading
  bind:this={liEl}
>
  <div class="loading-wrap" class:loading>
    <Spinner />
  </div>
  <section>
    <h4 class="pf-500">
      <div class="out-icon desk-only" style={`border: 1px solid ${colour};`}>
        <i class="icon" style={`background-color:${colour}; border: 1px solid ${colour};`} />
      </div>
      <p><span class="mob-only pf-600">Section: </span><span class="pf-600">{title}</span></p>
      <p class="pf-600 mob-only">
        {#if block.length}
          Block: {block.map(b => b.toUpperCase()).join(", ")}
        {/if}
      </p>
    </h4>
    <p class="pf-300 mrg">
      {#if limitedAvailability}
        Limited Availability
      {:else}
        <p class="pf-400 desk-only">
          {#if block.length}
            Block: {block.map(b => b.toUpperCase()).join(", ")}
          {/if}
        </p>
        <p class="pf-400">
          {qty} Ticket{qty > 1 ? "s" : ""} available
        </p>
      {/if}
    </p>
    <div class="icons-wr">
      <IconGen title={ticketFormat?.title} iconOnly={true} />
      {#if item.singleSeat}
        <IconGen title="Single seats" iconOnly={true} />
      {:else if qty > 1}
        <IconGen title="Seated together" iconOnly={true} />
      {/if}
    </div>
    <div class="desk-only">
      <Suggestion title={ticketFormat?.title} className={ticketFormat?.title} />
      {#if item.singleSeat}
        <Suggestion title="single-seats" className="single-seats" />
      {:else if qty > 1}
        <Suggestion title="seated-together" className="seated-together" />
      {/if}
    </div>
  </section>
  <aside>
    <div>
      <p>
        {#if offerPrice}
          <span class="pf-500" class:bar={offerPrice}> {utils.nt(proceedPrice, locale, currency)} </span>
          <br />
          <span class="pf-500"> {utils.nt(offerPrice, locale, currency)} </span>
        {:else}
          <p class="pf-700 grid price">
            <span> {utils.nt(proceedPrice, locale, currency)}</span>
            <span>per ticket</span>
          </p>
        {/if}
        <i class="arrow" style={`stroke: ${colour};`} />
      </p>
    </div>
  </aside>
</li>

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;
  .single-ticket {
    position: relative;
    display: grid;
    grid-template-columns: auto auto;
    transition: 0.2s ease-in-out;
    border-bottom: 1px solid #d9d9d9;
    padding: 0.3em 1.5em;
    padding: 10px 1em;
    gap: 0.5em;
    @include desktop {
      padding: 1.3em 1em;
      border: 1px solid #d9d9d9;
      border-radius: 0.7em;
      margin-bottom: 0.6em;
    }
    p,
    h4,
    section,
    aside {
      margin: 0;
    }

    i {
      display: block;
      width: 0.7em;
      height: 0.7em;
      opacity: 0.2;
    }
    h4 {
      display: grid;
      align-items: center;
      margin-bottom: 7px;
      @include desktop {
        font-size: 20px;
        grid-template-columns: 1em auto;
        margin-bottom: 0.2em;
      }
      p {
        font-size: 16px;
        @include desktop {
          font-size: 18px;
        }
      }
    }

    .out-icon {
      border-radius: 50%;
      width: 0.7em;
      height: 0.7em;
      margin: 0;
      padding: 0;
      overflow: hidden;
    }
    &:hover {
      .icon {
        opacity: 1 !important;
      }
    }
    .mrg {
      margin-bottom: 0.2em;
      p {
        margin-bottom: 7px;
        font-size: 14px;
        @include desktop {
          font-size: 16px;
          margin-bottom: 0.3em;
        }
      }
    }

    .icons-wr {
      display: flex;
      @include desktop {
        display: none;
      }
    }
    aside {
      display: grid;
      align-items: center;
      p {
        display: grid;
        grid-template-columns: auto 1em;
        text-align: right;
        @include desktop {
          align-items: center;
        }

        .arrow {
          display: block;
          width: 0.4em;
          height: 0.8em;
          background: url("../_assets/arrow-dark.svg");
          background-repeat: no-repeat;
          background-position: center;
          margin: 0.15em 0 0 0.2em;
          opacity: 1;
          @include desktop {
            width: 0.7em;
            height: 1em;
          }
        }

        .bar {
          text-decoration-line: line-through;
          text-decoration-color: red;
        }
      }
      .price {
        grid-template-columns: 1fr;
      }
      .price span:last-of-type {
        color: #999;
        text-align: right;
        font-size: 12px;
        line-height: 100%; /* 12px */
      }
    }
    &.loading {
      pointer-events: none;
      background: #dedede;
    }
    .loading-wrap {
      display: none;
      align-items: center;
      justify-content: center;
      &.loading {
        display: grid;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
      }
    }
  }
</style>
